import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { NavHeader } from "../components/NavHeader";
import { Footer } from "../components/Footer";
import { Disclaimer } from "../components/Disclaimer";

const FAQS = [
  {
    q: "Is Astrarium really free?",
    a: "Yes. No sign-up, no email, no credit card, no premium tier hiding behind a paywall. Enter your birth details and you receive a full personalised reading.",
  },
  {
    q: "What does an Astrarium reading cover?",
    a: "Seven chapters: career and calling, love and romance, family and home, the year ahead (Q1 to Q4), wealth and finance, health and vitality, and an honest look at your strengths and shadows. It closes with a lucky forecast (day, colour, number, gemstone, direction, compatible signs, power phrase). You can also ask the wheel any custom question.",
  },
  {
    q: "Do I need to enter my exact time of birth?",
    a: "Time of birth is optional. If you know it, the reading can gesture at your rising sign, the constellation climbing the horizon at the minute you were born. If you do not, the reading stays anchored to your sun sign, element, modality, ruling planet and place of birth. The almanac is full either way.",
  },
  {
    q: "Is the reading written by AI?",
    a: "The framework is traditional astrology. Your sun sign, element, modality and ruling planet are computed deterministically from your date of birth using the tropical-zodiac date ranges astrologers have agreed on for centuries. The prose is then composed by a modern language model trained on a wide body of astrological, mythological and psychological writing. We use the model as a translator from symbol to sentence, nothing more.",
  },
  {
    q: "How accurate is an AI astrology reading?",
    a: "Astrology is a symbolic language, not a science. A good reading is one that makes you think, not one that promises certainty. We do not claim to predict the future. We do try to be specific where the chart allows it, and honest where it does not.",
  },
  {
    q: "Can I share my reading with a friend?",
    a: "Yes. Tap Share my almanac and we mint a private, unguessable link that lives for seven days and then disappears. No account required to view it. If you have asked any questions through Ask the Wheel, those travel with the link too.",
  },
  {
    q: "What happens to my data?",
    a: "Readings you do not share live only in your browser session. Readings you share are stored on our server for exactly seven days and then automatically and permanently deleted. We do not sell, rent or hand your data to advertisers. Full notice on the privacy and legal page.",
  },
  {
    q: "Why do you use the tropical zodiac?",
    a: "Tropical zodiac dates are the standard in Western astrology and are based on the four seasons and the Sun's relationship to the Earth, not the visual position of constellations. Vedic astrologers use the sidereal system instead, which is a different but parallel tradition. Astrarium leans tropical for the sun sign while drawing on Vedic principles for elemental and planetary interpretation.",
  },
  {
    q: "Can I ask follow-up questions about a specific situation?",
    a: "Yes. Ask the Wheel is built for exactly that. Be specific. 'Should I take the job in Berlin or stay in Mumbai?' will get a sharper answer than 'What should I do with my career?'",
  },
  {
    q: "Is this religious or spiritual?",
    a: "Astrology predates most modern religions and sits alongside them, not against them. Astrarium does not push a worldview. It offers a language for self-reflection that has been used quietly for thousands of years.",
  },
];

const Item = ({ q, a, isOpen, onToggle, idx }) => (
  <div
    className="border-b border-white/[0.06]"
    data-testid={`faq-item-${idx}`}
  >
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-4 text-left py-5 sm:py-6 group"
      aria-expanded={isOpen}
    >
      <span className="font-display text-lg sm:text-2xl text-[#F5EBD8] leading-snug">{q}</span>
      <ChevronDown
        className={`h-5 w-5 text-stardust shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
      />
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <p className="text-slate-300 leading-relaxed text-base pb-5 sm:pb-6 max-w-2xl">{a}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const FaqPage = () => {
  const [open, setOpen] = useState(0);
  useEffect(() => {
    document.title = "FAQ · Astrarium";
  }, []);
  return (
    <>
      <NavHeader />
      <main
        data-testid="faq-page"
        className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 pt-28 sm:pt-32 pb-12"
      >
        <p className="text-stardust/70 uppercase tracking-[0.5em] text-[10px] mb-5 text-center">Common questions</p>
        <h1 className="font-display text-4xl sm:text-6xl text-[#F5EBD8] text-center leading-tight mb-5">
          Frequently asked<span className="text-stardust">.</span>
        </h1>
        <p className="font-serif-italic text-slate-300/90 text-lg sm:text-xl leading-relaxed text-center mb-14 max-w-2xl mx-auto">
          The questions people ask before they read, and a few they ask after.
        </p>

        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl px-5 sm:px-8 py-2">
          {FAQS.map((f, i) => (
            <Item
              key={f.q}
              idx={i}
              q={f.q}
              a={f.a}
              isOpen={open === i}
              onToggle={() => setOpen(open === i ? -1 : i)}
            />
          ))}
        </div>

        <Disclaimer />
      </main>
      <Footer />
    </>
  );
};
